import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './i18n/I18nContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CourseEditor from './pages/CourseEditor';
import PublishShare from './pages/PublishShare';
import Analytics from './pages/Analytics';
import Player from './pages/Player';
import Reels from './pages/Reels';
import CourseReel from './pages/CourseReel';
import SingleReel from './pages/SingleReel';
import UnifiedClientAdmin from './pages/UnifiedClientAdmin';
import UserManagement from './pages/admin/UserManagement';
import AuditLog from './pages/admin/AuditLog';
import RequireSuperAdmin from './components/admin/RequireSuperAdmin';
import './App.css';

// Feature flag for Unified Client Admin
const ENABLE_UNIFIED_CLIENT_ADMIN = process.env.REACT_APP_ENABLE_UNIFIED_CLIENT_ADMIN === 'true';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-xl font-semibold mb-2 text-gray-800">Something went wrong</h1>
            <p className="text-sm text-gray-500 mb-4">
              The page failed to load. Please try again or contact support.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Go to Dashboard
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="mt-4 p-4 bg-red-50 text-red-600 text-xs text-left overflow-auto rounded">
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const tiktokMode = process.env.REACT_APP_TIKTOK_MODE === 'true';
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      {/* Editor routes - support both /editor/:id and /courses/:id/edit */}
      <Route path="/editor/new" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
      <Route path="/editor/:id" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
      <Route path="/courses/new" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
      <Route path="/courses/:id/edit" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
      {/* Share/Publish routes - support both /share/:id and /courses/:id/publish */}
      <Route path="/share/:id" element={<ProtectedRoute><PublishShare /></ProtectedRoute>} />
      <Route path="/courses/:id/publish" element={<ProtectedRoute><PublishShare /></ProtectedRoute>} />
      {/* Analytics routes - support both /analytics/:id and /courses/:id/analytics */}
      <Route path="/analytics/:id" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/courses/:id/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      {/* Player */}
      <Route path="/p/:courseId" element={<Player />} />
      {/* Reels */}
      {tiktokMode && <Route path="/reels" element={<Reels />} />}
      {tiktokMode && <Route path="/reels/course/:playlistId" element={<CourseReel />} />}
      <Route path="/reel/:reelId" element={<SingleReel />} />
      {/* Unified Client Admin (feature flagged) */}
      {ENABLE_UNIFIED_CLIENT_ADMIN && (
        <Route path="/admin/unified-clients/:clientId" element={<ProtectedRoute><UnifiedClientAdmin /></ProtectedRoute>} />
      )}
      {/* Phase 3 — super_admin-only user management */}
      <Route path="/admin/users" element={<RequireSuperAdmin><UserManagement /></RequireSuperAdmin>} />
      <Route path="/admin/audit-log" element={<RequireSuperAdmin><AuditLog /></RequireSuperAdmin>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <Toaster position="top-center" />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;